import type { UseFormSetValue } from 'react-hook-form';

import { type CheckboxData, type CheckboxStateType, CheckboxState } from './types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getNestedValue<T extends Record<string, any>>(obj: T, path: string): unknown {
  return path.split('.').reduce((acc, key) => (acc && key in acc ? acc[key] : undefined), obj);
}

export function getEveryChildrenChecked(
  children: CheckboxData[],
  values: Record<string, boolean> | undefined,
) {
  if (!values) return false;

  for (const child of children) {
    const isValueFound = getNestedValue(values, child.name) as boolean | undefined;
    if (isValueFound === false) {
      return false;
    } else if (child.children) {
      if (!getEveryChildrenChecked(child.children, values)) {
        return false;
      }
    }
  }

  return true;
}

export function getSomeChildrenChecked(
  children: CheckboxData[],
  values: Record<string, boolean> | undefined,
) {
  if (!values) return false;

  for (const child of children) {
    const isValueFound = getNestedValue(values, child.name) as boolean | undefined;
    if (isValueFound !== false) {
      return true;
    } else if (child.children) {
      if (getSomeChildrenChecked(child.children, values)) {
        return true;
      }
    }
  }

  return false;
}

export function determineState(
  values: Record<string, boolean> | undefined,
  children: CheckboxData[],
) {
  if (!values || (typeof values === 'object' && Object.keys(values).length === 0)) {
    return CheckboxState.UNCHECKED;
  }

  const isChecked = getEveryChildrenChecked(children, values);
  const isUnchecked = !getSomeChildrenChecked(children, values) && !isChecked;

  if (isChecked) {
    return CheckboxState.CHECKED;
  } else if (isUnchecked) {
    return CheckboxState.UNCHECKED;
  }

  return CheckboxState.INDETERMINATE;
}

export function setNewState(
  name: string,
  newState: CheckboxStateType,
  setValue: UseFormSetValue<Record<string, boolean>>,
) {
  setValue(name, newState !== CheckboxState.UNCHECKED);
}

export function updateStateRecursively(
  values: Record<string, boolean>,
  setValue: UseFormSetValue<Record<string, boolean>>,
  children: CheckboxData[],
  newState: CheckboxStateType,
) {
  for (const child of children) {
    setNewState(child.name, newState, setValue);

    if (child.children) {
      updateStateRecursively(values, setValue, child.children, newState);
    }
  }
}

export function findNodeByName(name: string, data: CheckboxData[]): CheckboxData | undefined {
  for (const node of data) {
    if (node.name === name) {
      return node;
    }

    if (node.children) {
      const found = findNodeByName(name, node.children);
      if (found) {
        return found;
      }
    }
  }

  return undefined;
}

export function findParentNodeByName(name: string, data: CheckboxData[]): CheckboxData | undefined {
  for (const node of data) {
    if (node.children) {
      for (const child of node.children) {
        if (child.name === name) {
          return node;
        }

        const found = findParentNodeByName(name, node.children);
        if (found) {
          return found;
        }
      }
    }
  }

  return undefined;
}

export function propagateToParent(
  name: string,
  data: CheckboxData[],
  values: Record<string, boolean>,
  setValue: UseFormSetValue<Record<string, boolean>>,
) {
  const parent = findParentNodeByName(name, data);
  if (!parent?.children) {
    return;
  }

  const newState = determineState(values, parent.children);
  setNewState(parent.name, newState, setValue);

  propagateToParent(parent.name, data, values, setValue);
}
